import React from 'react';
import { recurringKey, FAMILY_DIRECT_KEY, MARIAN_KEY } from '../engine.js';
import { useGuides } from './GuidesContext.jsx';

/* v8 Path I — Override-visibility pattern.
   Goal: anywhere in FND where data MIGHT have been modified from its
   default via Settings or Model assumptions, a subtle gold dot + optional
   tooltip tells the user "this value has a model-state override".
   Makes the connection between Settings edits and downstream reports
   instantly visible, without cluttering the UI.

   Why this matters: model state is split across many places
   (piecewiseOverrides, fxRates, multipliers, accountVisibility,
   jmLoanRepaySchedule, jpMode, recurringOverrides, …). Users lose
   track of what they've changed. A one-dot signal fixes that.

   Rules of thumb:
   - Show the badge only when ACTUALLY different from default.
   - Tooltip names *which override* applies + where to edit it.
   - Zero layout shift — the badge slot is always present, transparent
     when inactive, gold when active. Keeps tables aligned.
*/

export const OVERRIDE_DOT =
  'inline-block w-[6px] h-[6px] rounded-full align-middle shadow-[0_0_6px_rgba(240,180,41,0.5)]';

export default function ModifiedBadge({ active, reason, compact = false }) {
  const { modifiedBadges } = useGuides();
  if (!modifiedBadges) return null;
  const base = `${OVERRIDE_DOT} transition-colors`;
  const tone = active ? 'bg-accent-gold' : 'bg-transparent';
  const label = active ? (reason || 'Modified from default in this model state') : '';
  return (
    <span
      className={`${base} ${tone} ${compact ? '' : 'ml-[6px]'}`}
      title={label}
      aria-label={label}
      role={active ? 'img' : 'presentation'}
    />
  );
}

/* Inline text variant — for compact rows where a dot gets lost.
   Example: a DataTable row footer or a dense list. */
export function ModifiedLabel({ active, reason, children }) {
  if (!active) return children;
  return (
    <span
      className="inline-flex items-center gap-[5px] text-accent-gold"
      title={reason || 'Modified from default in this model state'}
    >
      <span className={`${OVERRIDE_DOT} bg-accent-gold`} aria-hidden />
      {children}
    </span>
  );
}

/* ============================================================
   Helpers — ask these instead of hard-coding override checks in
   every consumer. Keeps the override rules in one place; when
   new customization paths land (new piecewise, new multiplier,
   new preset field), add the check here and every consumer
   benefits automatically.
   ============================================================ */

export function isRecurringOverridden(scenario, line) {
  const key = recurringKey(line);
  return !!(scenario?.piecewiseOverrides?.[key]);
}

export function isFamilyDirectOverridden(scenario) {
  return !!(scenario?.piecewiseOverrides?.[FAMILY_DIRECT_KEY]);
}

export function isMarianOverridden(scenario) {
  return !!(scenario?.piecewiseOverrides?.[MARIAN_KEY]);
}

export function isJMRepayOverridden(scenario) {
  return !!scenario?.jmLoanRepaySchedule;
}

export function isFXOverridden(scenario, currency) {
  return scenario?.fxRates?.[currency] != null;
}

export function isMultiplierOverridden(scenario, key) {
  const v = scenario?.multipliers?.[key];
  return v != null && Math.abs(v - 1) > 1e-9;
}

export function isAccountVisibilityCustomized(scenario) {
  return Object.keys(scenario?.accountVisibility || {}).length > 0;
}

/* v8 Path AL — piecewise pins for fixed-asset valuations + LTL
   outstanding balances. Each helper returns true when the user has
   at least one ym-pin configured for the given asset/liability id.
   Consumers (NC-Assets / NC-Liabilities tables, 3-Statement Model) show
   a gold dot + tooltip pointing to Drivers -> Model schedules. */
export function isAssetPinned(scenario, assetId) {
  const pins = scenario?.piecewiseAssets?.[assetId];
  return Array.isArray(pins) && pins.length > 0;
}

export function isLTLPinned(scenario, ltlId) {
  const pins = scenario?.piecewiseLTLs?.[ltlId];
  return Array.isArray(pins) && pins.length > 0;
}

/* Aggregate detector — any piecewise asset/liability pin set at all. Used
   by section-level badges. */
export function countAssetPins(scenario) {
  return Object.keys(scenario?.piecewiseAssets || {})
    .filter(id => Array.isArray(scenario.piecewiseAssets[id]) && scenario.piecewiseAssets[id].length > 0)
    .length;
}

export function countLTLPins(scenario) {
  return Object.keys(scenario?.piecewiseLTLs || {})
    .filter(id => Array.isArray(scenario.piecewiseLTLs[id]) && scenario.piecewiseLTLs[id].length > 0)
    .length;
}

/* Reason strings — keep in one place so tooltips read consistently. */
export const OVERRIDE_REASON = {
  recurring:       'Overridden via Drivers → Recurring Schedules (piecewise)',
  familyDirect:    'Overridden via Drivers → Model schedules (operating obligations)',
  marian:          'Overridden via Drivers -> Model schedules (routed obligation channel)',
  jmRepay:         'Overridden via Model assumptions -> financing repayment schedule',
  fx:              'Pinned via Model assumptions -> FX rates',
  multiplier:      'Tuned via Model assumptions -> Multipliers',
  accountVis:      'Per-account visibility customized in Settings → Account Visibility',
  assetPin:        'NC-Asset value pinned per month via Drivers -> Model schedules',
  ltlPin:          'NC-Liability outstanding balance pinned per month via Drivers -> Model schedules',
};
